import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { Column, ColumnKeys, BreachPathAppWebComponentSchema } from '../types';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GRID_FOOTER,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from 'apps/webcomponents/src/webcomponents.constants';
import { GenericGridSmartViewsWebComponent } from 'apps/webcomponents/src/grid/genericGridSmartViews';
import {
  GridViewWebComponent,
  GzButtonWebComponent,
  GzPushBodyLayoutWebComponent,
  GzSideMenuWebComponent,
  GzTextWebComponent,
} from 'apps/webcomponents/src';
import {
  getSavedSmartViewsCategory,
  setSmartViewsPanelSizeBasedOnState,
} from 'apps/webcomponents/src/grid/genericGridSmartViews/utils';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { ElementClass } from 'apps/webcomponents/src/utils/htmlElement.utils';
import { convertGenericViewConfigToGridViewConfig } from 'apps/webcomponents/src/grid/genericGridSmartViews/types';
import {
  processExternalFilters,
  setGridCommonProperties,
  getCompanyFilterUiConfig,
  getOSTypeColumnValues,
  getWatchlistColumnValues,
} from 'apps/webcomponents/src/riskManagement/common/utils';
import { BreachPathAppGridDataProvider } from '../dataProvider/BreachPathAppGridDataProvider';
import { ColumnDetails, ColumnType } from 'apps/webcomponents/src/grid/types/common';
import {
  issueNameRenderer,
  resourceNameRenderer,
  resourceRiskScoreRenderer,
  resourceIdRenderer,
  cvesCountRenderer,
  resourceTypeRenderer,
  platformRenderer,
  cloudAccountRenderer,
  regionsRenderer,
  lastUpdatedRenderer,
  firstFoundRenderer,
  actionsColumnRenderer,
} from './customGridRowRenderers';
import { DefaultGridColumnFixedWidths } from '@nirvana/shared/dtos/types/shared/grid';
import { BreachPathEvents } from '../../common/types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { companyRenderer } from '../../../riskManagement/common/customRenderers';
import { RemoteDetailsTypes } from 'apps/webcomponents/src/filters/backendEnumFilter/types';

export class BreachPathAppView extends BaseWebComponentView {
  public breachPathIssueId: string;
  private prevGridViewId: string;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$title = this.shadow.querySelector('gz-push-body-layout .title');
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');
    this.dom.$pageTitle = this.shadow.querySelector('.breach-path-header .title');

    this.dom.$pushBodyLayout = this.shadow.querySelector(
      WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT
    ) as GzPushBodyLayoutWebComponent;

    this.dom.$grid = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_VIEW}`
    ) as GridViewWebComponent<Column, ColumnKeys>;

    this.dom.$loadingErrorMessage = this.shadow.querySelector('gz-push-body-layout .loading-error--message');
    this.dom.$gridSmartViews = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS}`
    ) as GenericGridSmartViewsWebComponent;
    this.dom.$smartViewsTitle = this.shadow.querySelector('gz-push-body-layout #smartViewsTitle');
    this.dom.$sideMenu = this.shadow.querySelector('gz-push-body-layout #sideMenu') as GzSideMenuWebComponent;

    // Early access and feedback
    // this.dom.$earlyAccessLabel = this.shadow.querySelector('.early-access-container gz-text') as GzTextWebComponent;
    // this.dom.$feedbackFormButton = this.shadow.querySelector('#feedback-btn') as GzButtonWebComponent;
    // this.dom.$feedbackFormButtonText = this.shadow.querySelector('#feedback-btn-text');
    // this.dom.$feedbackFormButtonText.textContent = translateFn('Feedback');

    this.setGridActions();
    this.setTranslatedStrings();
  }

  async buildSuccessView(data: BreachPathAppWebComponentSchema) {
    // this.buildEarlyAccessSection();
    this.buildSmartViews().then(async () => {
      if (data.externalFilters?.length > 0) {
        await this.dom.$gridSmartViews.controller.setDefaultViewAsActiveView();
      }

      const activeView = this.dom.$gridSmartViews.controller.getActiveView();
      setSmartViewsPanelSizeBasedOnState(this.dom.$pushBodyLayout, this.dom.$gridSmartViews.smartViewState);
      await this.buildGrid(convertGenericViewConfigToGridViewConfig(activeView), data.externalFilters ?? []);
    });
  }

  private async buildSmartViews() {
    const smartViewDependencies = this.dependencies;
    smartViewDependencies.componentName = 'breach-path-grid';
    if (!(this.dom.$gridSmartViews instanceof GenericGridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }
    this.dom.$gridSmartViews.categoriesConfig = getSavedSmartViewsCategory(
      smartViewDependencies.componentName,
      this.getTranslations().defaultViewsTranslations,
      smartViewDependencies.api.clients.SmartViews.validateViewName
    );
    this.dom.$gridSmartViews.hasSearchInput = true;
    this.dom.$gridSmartViews.injectDependencies(smartViewDependencies);

    await this.dom.$gridSmartViews.start();
    this.removeElementClass(this.dom.$gridSmartViews, ElementClass.HIDDEN);
    this.setElementClass(this.dom.$loader, ElementClass.HIDDEN);
  }

  async buildGrid(view, externalFilters: Array<any> = []) {
    if (view.id === this.prevGridViewId && this.dom.$grid.gridLoaded) {
      return;
    }

    this.prevGridViewId = view.id;

    const {
      hasExternalFilters,
      initialViewWithoutExternalFilters,
      view: processedView,
    } = processExternalFilters(view, externalFilters);

    this.dom.$grid.propertiesSet = false;

    setGridCommonProperties(this.dom.$grid, processedView, hasExternalFilters, initialViewWithoutExternalFilters);

    this.dom.$grid.dataProvider = new BreachPathAppGridDataProvider(this.dependencies);
    this.dom.$grid.allColumnDetails = this.getGridColumns();
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.ISSUE_NAME]: issueNameRenderer.bind(this),
      [ColumnKeys.RESOURCE_NAME]: resourceNameRenderer.bind(this),
      [ColumnKeys.RESOURCE_RISK_SCORE]: resourceRiskScoreRenderer.bind(this),
      [ColumnKeys.RESOURCE_ID]: resourceIdRenderer.bind(this),
      [ColumnKeys.CVES_COUNT]: cvesCountRenderer.bind(this),
      [ColumnKeys.RESOURCE_TYPE]: resourceTypeRenderer.bind(this),
      [ColumnKeys.PLATFORM]: platformRenderer.bind(this),
      [ColumnKeys.CLOUD_ACCOUNT]: cloudAccountRenderer.bind(this),
      [ColumnKeys.REGIONS]: regionsRenderer.bind(this),
      [ColumnKeys.LAST_UPDATED]: lastUpdatedRenderer.bind(this),
      [ColumnKeys.FIRST_FOUND]: firstFoundRenderer.bind(this),
      [ColumnKeys.SELECT]: actionsColumnRenderer.bind(this),
    };
    this.dom.$grid.filtersUiConfigs = this.getFiltersUiConfig();
    this.dom.$grid.sideMenuParent = this.dom.$wrapper;
    this.dom.$grid.predefinedFiltersKeys = this.getPredefinedFiltersKeys();
    this.dom.$grid.enableRowClickEvent = true;
    this.dom.$grid.hasSelectableRows = false;
    this.dom.$grid.enableCSVExport = false;
    this.dom.$grid.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('No issues found'),
    };

    this.dom.$grid.propertiesSet = true;

    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies);
      await this.dom.$grid.start();
    }
  }

  private buildEarlyAccessSection(): void {
    this.dom.$earlyAccessLabel.innerText = translateFn('Early Access');
    this.dom.$feedbackFormButton.onclick = () => {
      this.dom.$feedbackFormButton.dispatchEvent(
        new CustomEvent(BreachPathEvents.OPEN_FEEDBACK_FORM, {
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
    };
  }

  getTranslations() {
    return {
      smartViewsStrings: this.stringsTranslator.getSmartViewsTranslations(),
      defaultViewsTranslations: {
        all_issues: translateFn('All breach paths'),
        breach_path_watchlist: translateFn('Watchlist'),
      },
    };
  }

  setTranslatedStrings(): void {
    this.dom.$pageTitle.innerHTML = this.dependencies.translateFn('Breach Path');
    this.dom.$smartViewsTitle.innerHTML = this.getTranslations().smartViewsStrings.genericPanelTitle.toUpperCase();
  }

  private getPredefinedFiltersKeys(): Array<ColumnKeys> {
    const keys: any = [];

    if (!Application.Main.company.isCustomer()) {
      keys.push(ColumnKeys.COMPANY);
    }

    return [
      ...keys,
      ColumnKeys.RESOURCE_NAME,
      ColumnKeys.LAST_UPDATED,
    ];
  }

  private getFiltersUiConfig() {
    return [
      getCompanyFilterUiConfig(ColumnKeys.COMPANY),
      {
        key: ColumnKeys.LAST_UPDATED,
        config: {
          additionalTimeRangeOptions: [
            { label: 'Last 90 days', value: 90 },
            { label: 'Last 180 days', value: 180 },
          ],
          enableSelectAllToDate: true,
        },
      },
      {
        key: ColumnKeys.FIRST_FOUND,
        config: {
          additionalTimeRangeOptions: [
            { label: 'Last 90 days', value: 90 },
            { label: 'Last 180 days', value: 180 },
          ],
          enableSelectAllToDate: true,
        },
      },
    ];
  }

  private setGridActions() {
    this.dom.$gridHeaderActions = this.shadow.querySelector('[slot=grid-header-actions-slot]');
  }

  protected getGridColumns(): Array<ColumnDetails<Column, ColumnKeys>> {
    const columns: Array<ColumnDetails<Column, ColumnKeys>> = [
      {
        column: Column.ISSUE_NAME,
        key: ColumnKeys.ISSUE_NAME,
        columnName: translateFn('Breach path name'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.XL,
        removable: false,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.RESOURCE_NAME,
        key: ColumnKeys.RESOURCE_NAME,
        columnName: translateFn('Main resource name'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        width: DefaultGridColumnFixedWidths.M,
        removable: false,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.BreachPathResourceNames,
        },
      },
      {
        column: Column.RESOURCE_RISK_SCORE,
        key: ColumnKeys.RESOURCE_RISK_SCORE,
        columnName: translateFn('Resource risk score'),
        type: ColumnType.RANGE,
        width: DefaultGridColumnFixedWidths.S,
        removable: true,
        sortable: true,
      },
      {
        column: Column.RESOURCE_ID,
        key: ColumnKeys.RESOURCE_ID,
        columnName: translateFn('Resource ID'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.M,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.CVES_COUNT,
        key: ColumnKeys.CVES_COUNT,
        columnName: translateFn('CVEs'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.XS,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.RESOURCE_TYPE,
        key: ColumnKeys.RESOURCE_TYPE,
        columnName: translateFn('Resource type'),
        type: ColumnType.STRING,
        width: DefaultGridColumnFixedWidths.S,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.PLATFORM,
        key: ColumnKeys.PLATFORM,
        columnName: translateFn('Platform'),
        type: ColumnType.ENUM,
        values: getOSTypeColumnValues(),
        width: DefaultGridColumnFixedWidths.XS,
        removable: true,
        sortable: false,
      },
      {
        column: Column.CLOUD_ACCOUNT,
        key: ColumnKeys.CLOUD_ACCOUNT,
        columnName: translateFn('Cloud account'),
        type: ColumnType.COMPLEX_ENUM_FILTER,
        width: DefaultGridColumnFixedWidths.S,
        removable: true,
        sortable: false,
        remoteDetails: {
          type: RemoteDetailsTypes.BreachPathCloudAccountIds,
        },
      },
      {
        column: Column.REGIONS,
        key: ColumnKeys.REGIONS,
        columnName: translateFn('Region'),
        type: ColumnType.STRING,
        width: '136px',
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
      {
        column: Column.LAST_UPDATED,
        key: ColumnKeys.LAST_UPDATED,
        columnName: translateFn('Last updated on'),
        type: ColumnType.DATE,
        width: DefaultGridColumnFixedWidths.S,
        removable: false,
        sortable: false,
        showTimePickers: false,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        column: Column.FIRST_FOUND,
        key: ColumnKeys.FIRST_FOUND,
        columnName: translateFn('Created on'),
        type: ColumnType.DATE,
        width: DefaultGridColumnFixedWidths.S,
        removable: true,
        sortable: false,
        showTimePickers: false,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.ALL_TO_DATE,
          },
        },
      },
      {
        column: Column.IS_WATCHLIST,
        key: ColumnKeys.IS_WATCHLIST,
        columnName: translateFn('In watchlist'),
        type: ColumnType.ENUM,
        values: getWatchlistColumnValues(),
        width: '100px',
        removable: true,
        sortable: false,
      },
      {
        column: Column.SELECT,
        key: ColumnKeys.SELECT,
        columnName: '',
        type: ColumnType.ACTION,
        removable: false,
        sortable: false,
        width: '100px',
        filteringDisabled: true,
      },
    ];

    if (Application.Main.company.isPartner()) {
      columns.unshift({
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: true,
        sortable: false,
        filterResetValue: {
          exactMatch: Application.Main.getUserCompany()?.id,
        },
      });
    }

    return columns;
  }
}
